/**
 * PII flag vocabulary, mirrored from `internal/models/pii.go`
 * (spec 2026-08-13-02).
 *
 * Type level (document types, contract types) carries the bare `pii` policy
 * token. Instance level (documents, contracts) carries AT MOST ONE `pii:*`
 * token: `pii:presumed` is machine-managed, `pii:confirmed` / `pii:none` are
 * sticky human verdicts.
 */

export const PII_FLAG = 'pii'
export const PII_PRESUMED = 'pii:presumed'
export const PII_CONFIRMED = 'pii:confirmed'
export const PII_NONE = 'pii:none'

/** The four states the tri-state badge renders. */
export type PIIState = 'confirmed' | 'presumed' | 'none' | 'unassessed'

/** The verdict values accepted by the `pii_status` PATCH field. */
export type PIIStatus = 'confirmed' | 'none' | 'clear'

/** The `pii` list-filter values. `undefined` means "no filter". */
export type PIIFilter = 'true' | 'false' | 'unknown'

/** True when a *type* carries the bare policy token. */
export function typeHasPII(flags?: string[] | null): boolean {
    return Array.isArray(flags) && flags.includes(PII_FLAG)
}

/**
 * Reads an instance's PII state off its flags. Human verdicts win over the
 * presumption — a row should never carry both, but reading in this order means
 * a legacy row that somehow does still reports the human answer.
 */
export function piiStateOf(flags?: string[] | null): PIIState {
    if (!Array.isArray(flags)) return 'unassessed'
    if (flags.includes(PII_CONFIRMED)) return 'confirmed'
    if (flags.includes(PII_NONE)) return 'none'
    if (flags.includes(PII_PRESUMED)) return 'presumed'
    return 'unassessed'
}

/** Effective "is PII" — what `pii=true` matches server-side. */
export function isEffectivePII(flags?: string[] | null): boolean {
    const state = piiStateOf(flags)
    return state === 'confirmed' || state === 'presumed'
}

/**
 * Adds or removes the bare `pii` policy token on a *type*'s flags, preserving
 * every other flag (a type may also carry `milestone`, `PUBLIC`, …).
 */
export function withTypePIIFlag(flags: string[] | undefined | null, enabled: boolean): string[] {
    const current = Array.isArray(flags) ? flags : []
    if (enabled) {
        return current.includes(PII_FLAG) ? [...current] : [...current, PII_FLAG]
    }
    return current.filter((f) => f !== PII_FLAG)
}
